import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import * as signalR from "@microsoft/signalr";
import moment from "moment";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import { getCurrentUser } from "../../../utils/userStorage";

const PAGE_SIZE = 20;
const FILTER_UNREAD = "unread";
const FILTER_ALL = "all";

const API_BASE_URL =
  process.env.REACT_APP_Environment === "development"
    ? process.env.REACT_APP_API_URL
    : process.env.REACT_APP_API_URL_Production;

const getApiBase = () => String(API_BASE_URL || "").replace(/\/?$/, "/");

const pick = (obj, ...keys) => {
  if (!obj || typeof obj !== "object") return undefined;
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
  }
  return undefined;
};

const normalizeNotification = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const id = pick(raw, "Notification_ID", "notification_ID", "notificationId", "NotificationId", "id");
  if (id == null) return null;
  const invoice = pick(
    raw,
    "Recvorder_InvoiceNo",
    "recvorder_InvoiceNo",
    "InvoiceNo",
    "invoiceNo",
    "invoice"
  );
  return {
    Notification_ID: Number(id),
    Title: String(pick(raw, "Title", "title") ?? "New order"),
    Body: String(pick(raw, "Body", "body") ?? ""),
    Recvorder_InvoiceNo: invoice != null ? String(invoice) : "",
    OrderCompID: Number(pick(raw, "OrderCompID", "orderCompID", "orderCompId") ?? 0) || 0,
    BranchName: String(pick(raw, "BranchName", "branchName") ?? ""),
    IsRead: Boolean(pick(raw, "IsRead", "isRead") ?? false),
    CreatedDateTime: pick(raw, "CreatedDateTime", "createdDateTime", "CreatedAt", "createdAt") ?? null,
    ReadDateTime: pick(raw, "ReadDateTime", "readDateTime") ?? null,
    Recvorder_ID: Number(pick(raw, "Recvorder_ID", "recvorder_ID", "recvorderId") ?? 0) || 0,
  };
};

const extractList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  const nested = pick(
    payload,
    "Items",
    "items",
    "Notifications",
    "notifications",
    "Data",
    "data",
    "List",
    "list"
  );
  return Array.isArray(nested) ? nested : [];
};

const extractUnreadCount = (payload) => {
  if (typeof payload === "number") return payload;
  if (payload == null) return 0;
  if (typeof payload === "object") {
    const count = pick(
      payload,
      "UnreadCount",
      "unreadCount",
      "Count",
      "count",
      "TotalUnread",
      "totalUnread"
    );
    if (typeof count === "number") return count;
  }
  const n = Number(payload);
  return Number.isFinite(n) ? n : 0;
};

function DropdownNotification() {
  const navigate = useNavigate();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const profileData = useSelector((state) => state.auth.profileData);
  const parentCompId = profileData?.parentComID ?? profileData?.ParentComID ?? 0;
  const isMain = !parentCompId || parseInt(parentCompId, 10) <= 0;
  const companyName = profileData?.compName ?? profileData?.CompName ?? "Parent";

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterMode, setFilterMode] = useState(FILTER_UNREAD);
  const [branchFilter, setBranchFilter] = useState("");
  const [branches, setBranches] = useState([]);
  const [markingAll, setMarkingAll] = useState(false);
  const [branchMenuOpen, setBranchMenuOpen] = useState(false);

  const hubRef = useRef(null);
  const filterModeRef = useRef(filterMode);
  const branchFilterRef = useRef(branchFilter);
  const branchMenuRef = useRef(null);

  useEffect(() => {
    filterModeRef.current = filterMode;
  }, [filterMode]);

  useEffect(() => {
    branchFilterRef.current = branchFilter;
  }, [branchFilter]);

  useEffect(() => {
    if (!branchMenuOpen) return undefined;
    const onDocMouseDown = (e) => {
      if (branchMenuRef.current && !branchMenuRef.current.contains(e.target)) {
        setBranchMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [branchMenuOpen]);

  useEffect(() => {
    setBranchMenuOpen(false);
  }, [filterMode]);

  const orderCompIdParam = useMemo(() => {
    if (!branchFilter) return undefined;
    const n = parseInt(branchFilter, 10);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, [branchFilter]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_STAFF_ORDER_UNREAD_COUNT);
      const data = response.data?.data ?? response.data?.Data ?? response.data;
      setUnreadCount(extractUnreadCount(data));
    } catch (err) {
      // Backend may not be deployed yet; keep badge at last known value.
      console.warn("GetUnreadCount failed:", err?.response?.data?.message || err?.message);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        PageNumber: 1,
        PageSize: PAGE_SIZE,
      };
      if (filterModeRef.current === FILTER_UNREAD) {
        params.IsRead = false;
      }
      if (branchFilterRef.current) {
        const n = parseInt(branchFilterRef.current, 10);
        if (Number.isFinite(n) && n > 0) params.OrderCompID = n;
      }

      const response = await axiosInstance.get(APIs.GET_STAFF_ORDER_NOTIFICATIONS, { params });
      const payload = response.data?.data ?? response.data?.Data ?? response.data;
      const list = extractList(payload)
        .map(normalizeNotification)
        .filter(Boolean);
      setNotifications(list);
    } catch (err) {
      console.warn("GetNotifications failed:", err?.response?.data?.message || err?.message);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBranches = useCallback(async () => {
    if (!isMain || !CompID) {
      setBranches([]);
      return;
    }
    try {
      const response = await axiosInstance.get(APIs.GET_COMPANY_LOCATIONS, {
        params: { CompID },
      });
      const locationData = response.data?.data ?? response.data ?? [];
      const arr = Array.isArray(locationData) ? locationData : [];
      const parentId = typeof CompID === "number" ? CompID : parseInt(CompID, 10);
      const opts = [{ value: String(parentId), label: companyName }];
      arr.forEach((loc) => {
        const pid = loc.ParentComID ?? loc.parentComID;
        const id = loc.CompID ?? loc.compID;
        const pidNum = pid != null ? Number(pid) : null;
        const idNum = id != null ? Number(id) : null;
        if (idNum == null || Number.isNaN(idNum)) return;
        if (pidNum === parentId && idNum !== parentId) {
          const name = String(loc.CompName ?? loc.compName ?? "").trim() || `Branch ${idNum}`;
          opts.push({ value: String(idNum), label: name });
        }
      });
      setBranches(opts);
    } catch (err) {
      console.warn("Branch list for notifications failed:", err?.message);
      setBranches([]);
    }
  }, [CompID, isMain, companyName]);

  const refreshInboxFromServer = useCallback(() => {
    // SignalR group payload uses NotificationId=0 (shared); never use it for MarkRead.
    fetchUnreadCount();
    fetchNotifications();
  }, [fetchUnreadCount, fetchNotifications]);

  const connectHub = useCallback(() => {
    if (hubRef.current) return;

    const hubPath = String(APIs.HUB_BACKOFFICE_ORDER_NOTIFICATION || "").replace(/^\//, "");
    const hubUrl = `${getApiBase()}${hubPath}`.replace(/([^:]\/)\/+/g, "$1");

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => getCurrentUser()?.token || "",
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection.on("ParentAdminOrderNotification", () => {
      refreshInboxFromServer();
    });

    connection.onreconnected(() => {
      refreshInboxFromServer();
    });

    hubRef.current = connection;

    connection
      .start()
      .catch((err) => {
        console.warn("BackOfficeOrderNotification hub connect failed:", err?.message || err);
        hubRef.current = null;
      });
  }, [refreshInboxFromServer]);

  const disconnectHub = useCallback(() => {
    const connection = hubRef.current;
    hubRef.current = null;
    if (connection) {
      connection.stop().catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      disconnectHub();
      setNotifications([]);
      setUnreadCount(0);
      return undefined;
    }

    fetchUnreadCount();
    fetchNotifications();
    fetchBranches();
    connectHub();

    return () => {
      disconnectHub();
    };
  }, [
    isAuthenticated,
    fetchUnreadCount,
    fetchNotifications,
    fetchBranches,
    connectHub,
    disconnectHub,
  ]);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchNotifications();
  }, [filterMode, branchFilter, isAuthenticated, fetchNotifications]);

  const handleMarkRead = async (item, { navigateAfter = true } = {}) => {
    if (!item?.Notification_ID) return;

    const wasUnread = !item.IsRead;
    setNotifications((prev) => {
      if (filterMode === FILTER_UNREAD) {
        return prev.filter((n) => n.Notification_ID !== item.Notification_ID);
      }
      return prev.map((n) =>
        n.Notification_ID === item.Notification_ID
          ? { ...n, IsRead: true, ReadDateTime: new Date().toISOString() }
          : n
      );
    });
    if (wasUnread) {
      setUnreadCount((c) => Math.max(0, c - 1));
    }

    try {
      await axiosInstance.post(APIs.POST_STAFF_ORDER_MARK_READ, {
        notificationId: item.Notification_ID,
      });
    } catch (err) {
      console.warn("MarkRead failed:", err?.response?.data?.message || err?.message);
      fetchUnreadCount();
      fetchNotifications();
    }

    if (navigateAfter && item.Recvorder_InvoiceNo) {
      navigate(`/Order/orderDetails/${encodeURIComponent(item.Recvorder_InvoiceNo)}`);
    }
  };

  const handleMarkAllRead = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (markingAll || unreadCount <= 0) return;

    setMarkingAll(true);
    try {
      const body = {};
      if (orderCompIdParam) body.orderCompID = orderCompIdParam;
      await axiosInstance.post(APIs.POST_STAFF_ORDER_MARK_ALL_READ, body);

      if (orderCompIdParam) {
        // Branch-scoped mark-all: refresh badge from server (other branches may still be unread)
        await fetchUnreadCount();
        await fetchNotifications();
      } else {
        setUnreadCount(0);
        if (filterMode === FILTER_UNREAD) {
          setNotifications([]);
        } else {
          setNotifications((prev) =>
            prev.map((n) => ({
              ...n,
              IsRead: true,
              ReadDateTime: n.ReadDateTime || new Date().toISOString(),
            }))
          );
        }
      }
    } catch (err) {
      console.warn("MarkAllRead failed:", err?.response?.data?.message || err?.message);
      fetchUnreadCount();
      fetchNotifications();
    } finally {
      setMarkingAll(false);
    }
  };

  const keepMenuOpen = (e) => {
    // Keep Bootstrap dropdown open; do not preventDefault — that blocks <select> opening.
    e.stopPropagation();
  };

  const formatTime = (value) => {
    if (!value) return "";
    const m = moment(value);
    return m.isValid() ? m.fromNow() : "";
  };

  const displayCount = unreadCount > 99 ? "99+" : String(unreadCount);
  const emptyTitle =
    filterMode === FILTER_UNREAD ? "You're all caught up" : "No notifications yet";
  const emptyText =
    filterMode === FILTER_UNREAD
      ? isMain
        ? "New order alerts for your branches will show up here."
        : "New order alerts for this branch will show up here."
      : "When orders come in, you'll see them in this inbox.";
  const selectedBranchLabel =
    branches.find((b) => b.value === branchFilter)?.label || "All branches";

  const selectBranch = (value) => {
    setBranchFilter(value);
    setBranchMenuOpen(false);
  };

  return (
    <div className="navbar-item dropdown">
      <a
        href="#/"
        data-bs-toggle="dropdown"
        data-bs-auto-close="outside"
        className="navbar-link notification-bell"
        aria-label={unreadCount > 0 ? `${unreadCount} unread notifications` : "Notifications"}
      >
        <i className="fa fa-bell"></i>
        <span className={`badge ${unreadCount > 0 ? "has-unread" : "is-empty"}`}>
          {displayCount}
        </span>
      </a>

      <div className="dropdown-menu dropdown-menu-end notification-panel">
        <div className="np-header">
          <div className="np-header-left">
            <h5 className="np-header-title">Notifications</h5>
            <p className="np-header-meta">
              {unreadCount > 0
                ? `${unreadCount} unread`
                : "No unread alerts"}
            </p>
          </div>
          <div className="np-header-right" onMouseDown={keepMenuOpen}>
            <button
              type="button"
              className="np-mark-all"
              disabled={markingAll || unreadCount <= 0}
              onClick={handleMarkAllRead}
            >
              {markingAll ? "Marking…" : "Mark all read"}
            </button>
            <div className="np-tabs" role="tablist" aria-label="Notification filter">
              <button
                type="button"
                role="tab"
                aria-selected={filterMode === FILTER_UNREAD}
                className={`np-tab ${filterMode === FILTER_UNREAD ? "is-active" : ""}`}
                onClick={() => setFilterMode(FILTER_UNREAD)}
              >
                Unread
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={filterMode === FILTER_ALL}
                className={`np-tab ${filterMode === FILTER_ALL ? "is-active" : ""}`}
                onClick={() => setFilterMode(FILTER_ALL)}
              >
                All
              </button>
            </div>
          </div>
        </div>

        {isMain && branches.length > 0 && (
          <div className="np-toolbar" onMouseDown={keepMenuOpen}>
            <div className={`np-branch-picker ${branchMenuOpen ? "is-open" : ""}`} ref={branchMenuRef}>
              <button
                type="button"
                className="np-branch-trigger"
                aria-haspopup="listbox"
                aria-expanded={branchMenuOpen}
                aria-label="Filter by branch"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setBranchMenuOpen((open) => !open);
                }}
              >
                <span className="np-branch-label">{selectedBranchLabel}</span>
                <i className={`fa fa-chevron-${branchMenuOpen ? "up" : "down"}`} aria-hidden="true"></i>
              </button>
              {branchMenuOpen && (
                <ul className="np-branch-menu" role="listbox" aria-label="Branches">
                  <li role="option" aria-selected={branchFilter === ""}>
                    <button
                      type="button"
                      className={`np-branch-option ${branchFilter === "" ? "is-selected" : ""}`}
                      onClick={() => selectBranch("")}
                    >
                      All branches
                    </button>
                  </li>
                  {branches.map((b) => (
                    <li key={b.value} role="option" aria-selected={branchFilter === b.value}>
                      <button
                        type="button"
                        className={`np-branch-option ${branchFilter === b.value ? "is-selected" : ""}`}
                        onClick={() => selectBranch(b.value)}
                      >
                        {b.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        <div className="np-list">
          {loading && notifications.length === 0 ? (
            <div className="np-loading" role="status">
              <div className="np-loading-icon">
                <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
              </div>
              <p className="np-loading-text">Loading notifications…</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="np-empty">
              <div className="np-empty-icon" aria-hidden="true">
                <i className="fa fa-bell-slash"></i>
              </div>
              <p className="np-empty-title">{emptyTitle}</p>
              <p className="np-empty-text">{emptyText}</p>
            </div>
          ) : (
            notifications.map((item) => {
              const branchLabel =
                item.BranchName || (item.OrderCompID ? `Branch ${item.OrderCompID}` : "Order");
              const titleLine = item.Recvorder_InvoiceNo
                ? `${item.Title} · #${item.Recvorder_InvoiceNo}`
                : item.Title;

              return (
                <a
                  key={item.Notification_ID}
                  href="#/"
                  className={`np-item ${item.IsRead ? "" : "is-unread"}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleMarkRead(item);
                  }}
                >
                  <div className={`np-item-icon ${item.IsRead ? "is-read" : ""}`} aria-hidden="true">
                    <i className="fa fa-shopping-bag"></i>
                  </div>
                  <div className="np-item-body">
                    <div className="np-item-top">
                      <span className="np-item-branch">{branchLabel}</span>
                      <span className="np-item-time">{formatTime(item.CreatedDateTime)}</span>
                    </div>
                    <p className="np-item-title">{titleLine}</p>
                    {item.Body ? <p className="np-item-body-text">{item.Body}</p> : null}
                  </div>
                  {!item.IsRead ? <span className="np-item-dot" aria-label="Unread" /> : null}
                </a>
              );
            })
          )}
        </div>

        <div className="np-footer">
          <a
            href="#/"
            className="np-footer-link"
            onClick={(e) => {
              e.preventDefault();
              navigate("/Order/manageOrder");
            }}
          >
            View all orders
            <i className="fa fa-arrow-right" aria-hidden="true"></i>
          </a>
        </div>
      </div>
    </div>
  );
}

export default DropdownNotification;
